export const useAudio = (): [(url: string, callback: Function) => void] => {
    const playAudio = (url: string, callback: Function): void => {
        const audio = new Audio(url);
        audio.loop = false;
        void audio.play();
        audio.onended = () => {
            callback();
        };
    };

    return [playAudio];
};

export interface AudioPlaybackAttemptDiagnostic {
    attempt: number;
    played: boolean;
    elapsed: number;
    reason: string;
    audioErrorCode?: number;
    audioErrorMessage?: string;
    errorName?: string;
    errorMessage?: string;
    networkState: number;
    readyState: number;
    src: string;
}

interface AudioPlaybackResult extends Omit<AudioPlaybackAttemptDiagnostic, "attempt"> {}

const getMediaErrorMessage = (code?: number): string | undefined => {
    switch (code) {
        case 1:
            return "MEDIA_ERR_ABORTED";
        case 2:
            return "MEDIA_ERR_NETWORK";
        case 3:
            return "MEDIA_ERR_DECODE";
        case 4:
            return "MEDIA_ERR_SRC_NOT_SUPPORTED";
        default:
            return undefined;
    }
};

const getPlaybackResult = (
    audio: HTMLAudioElement,
    played: boolean,
    reason: string,
    startedAt: number,
    error?: unknown
): AudioPlaybackResult => {
    const mediaErrorCode = audio.error?.code;
    const getErrorMessage = (): string | undefined => {
        if (!error) return undefined;
        if (error instanceof Error) return error.message;
        if (typeof error === "string") return error;

        try {
            return JSON.stringify(error);
        } catch {
            return "Unknown playback error";
        }
    };

    return {
        played,
        reason,
        elapsed: Date.now() - startedAt,
        audioErrorCode: mediaErrorCode,
        audioErrorMessage: getMediaErrorMessage(mediaErrorCode),
        errorName: error instanceof Error ? error.name : undefined,
        errorMessage: getErrorMessage(),
        networkState: audio.networkState,
        readyState: audio.readyState,
        src: audio.currentSrc || audio.src
    };
};

export const playAudioWithDiagnosticsAsync = async (
    audio: HTMLAudioElement,
    timeout = 10000
): Promise<AudioPlaybackResult> => {
    return await new Promise((resolve) => {
        let settled = false;
        let timeoutId: number | undefined;
        const startedAt = Date.now();

        const finish = (played: boolean, reason: string, error?: unknown): void => {
            if (settled) return;

            settled = true;
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }

            audio.onended = null;
            audio.onerror = null;
            audio.onstalled = null;
            audio.onabort = null;
            resolve(getPlaybackResult(audio, played, reason, startedAt, error));
        };

        try {
            timeoutId = window.setTimeout(() => finish(false, "timeout"), timeout);
            audio.onended = () => finish(true, "ended");
            audio.onerror = () => finish(false, "error");
            audio.onstalled = () => finish(false, "stalled");
            audio.onabort = () => finish(false, "abort");
            void audio.play().catch((error) => finish(false, "play-rejected", error));
        } catch (er) {
            finish(false, "exception", er);
        }
    });
};

export const playAudioAsync = async (audio: HTMLAudioElement, timeout = 10000): Promise<boolean> => {
    return (await playAudioWithDiagnosticsAsync(audio, timeout)).played;
};

export const playAudioWithRetryAsync = async (
    audioFactory: () => HTMLAudioElement,
    retries = 2,
    timeout = 10000,
    onAttempt?: (diagnostic: AudioPlaybackAttemptDiagnostic) => void,
    retryDelay = 0
): Promise<boolean> => {
    for (let attempt = 0; attempt <= retries; attempt++) {
        const audio = audioFactory();
        const result = await playAudioWithDiagnosticsAsync(audio, timeout);
        onAttempt?.({
            attempt: attempt + 1,
            ...result
        });

        if (result.played) {
            return true;
        }

        audio.load();

        if (retryDelay > 0 && attempt < retries) {
            await new Promise((resolve) => window.setTimeout(resolve, retryDelay));
        }
    }

    return false;
};
